import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart3, PieChart, LineChart, Activity, Target, Zap, Star, Share2, Search, Grid, List, Globe, Plus, Edit3, Trash2, FileText, Circle, FileText as FileTextIcon } from 'lucide-react';

interface ChartData {
  id: string;
  type: 'bar' | 'line' | 'pie' | 'area' | 'scatter' | 'radar' | 'doughnut' | 'polar';
  title: string;
  description: string;
  data: any[];
  labels: string[];
  colors: string[];
  options: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    plugins: {
      legend: boolean;
      title: boolean;
    };
    scales?: any;
  };
  createdAt: Date;
  updatedAt: Date;
  author: string;
  isPublic: boolean;
  tags: string[];
}

interface DataVisualizationProps {
  articleId: string;
  onChartCreate?: (chart: ChartData) => void;
  onChartUpdate?: (chart: ChartData) => void;
  onChartDelete?: (chartId: string) => void;
  onChartShare?: (chart: ChartData) => void;
  onChartExport?: (chart: ChartData, format: 'png' | 'svg' | 'pdf' | 'csv') => void;
}

const DataVisualization: React.FC<DataVisualizationProps> = ({
  articleId,
  onChartCreate,
  onChartUpdate,
  onChartDelete,
  onChartShare,
  onChartExport
}) => {
  const [charts, setCharts] = useState<ChartData[]>([]);
  const [selectedChart, setSelectedChart] = useState<ChartData | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newChart, setNewChart] = useState<Partial<ChartData>>({
    type: 'bar',
    title: '',
    description: '',
    data: [],
    labels: [],
    colors: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: true,
        title: true
      }
    },
    isPublic: false,
    tags: []
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    loadCharts();
  }, [articleId]);

  const loadCharts = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would fetch from your backend
      const sampleCharts: ChartData[] = [
        {
          id: '1',
          type: 'bar',
          title: 'Reading Speed Over Time',
          description: 'Track your reading speed improvements',
          data: [120, 150, 180, 200, 220, 250, 280],
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7'],
          colors: ['#3B82F6'],
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: true,
              title: true
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Words per Minute'
                }
              }
            }
          },
          createdAt: new Date('2024-01-01'),
          updatedAt: new Date('2024-01-15'),
          author: 'current-user',
          isPublic: true,
          tags: ['reading', 'progress', 'analytics']
        },
        {
          id: '2',
          type: 'pie',
          title: 'Content Categories',
          description: 'Distribution of content types read',
          data: [35, 25, 20, 15, 5],
          labels: ['News', 'Technology', 'Science', 'Business', 'Entertainment'],
          colors: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'],
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: true,
              title: true
            }
          },
          createdAt: new Date('2024-01-05'),
          updatedAt: new Date('2024-01-10'),
          author: 'current-user',
          isPublic: true,
          tags: ['categories', 'distribution', 'content']
        },
        {
          id: '3',
          type: 'line',
          title: 'Comprehension Scores',
          description: 'Your comprehension improvement over time',
          data: [65, 70, 75, 80, 85, 88, 90],
          labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
          colors: ['#10B981'],
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: true,
              title: true
            },
            scales: {
              y: {
                beginAtZero: true,
                max: 100,
                title: {
                  display: true,
                  text: 'Comprehension %'
                }
              }
            }
          },
          createdAt: new Date('2024-01-10'),
          updatedAt: new Date('2024-01-20'),
          author: 'current-user',
          isPublic: false,
          tags: ['comprehension', 'learning', 'progress']
        }
      ];
      setCharts(sampleCharts);
    } catch (err) {
      setError('Failed to load charts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateChart = () => {
    if (!newChart.title || !newChart.data || !newChart.data.length) return;

    const chart: ChartData = {
      id: Date.now().toString(),
      type: newChart.type as any,
      title: newChart.title,
      description: newChart.description || '',
      data: newChart.data,
      labels: newChart.labels || [],
      colors: newChart.colors || ['#3B82F6'],
      options: newChart.options || {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: true,
          title: true
        }
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      author: 'current-user',
      isPublic: newChart.isPublic || false,
      tags: newChart.tags || []
    };

    setCharts(prev => [...prev, chart]);
    onChartCreate?.(chart);
    setShowCreateModal(false);
    setNewChart({
      type: 'bar',
      title: '',
      description: '',
      data: [],
      labels: [],
      colors: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: true,
          title: true
        }
      },
      isPublic: false,
      tags: []
    });
  };

  const handleUpdateChart = (updatedChart: ChartData) => {
    setCharts(prev => prev.map(chart => 
      chart.id === updatedChart.id ? updatedChart : chart
    ));
    onChartUpdate?.(updatedChart);
    setShowEditModal(false);
    setSelectedChart(null);
  };

  const handleDeleteChart = (chartId: string) => {
    setCharts(prev => prev.filter(chart => chart.id !== chartId));
    onChartDelete?.(chartId);
    setSelectedChart(null);
  };

  const handleShareChart = (chart: ChartData) => {
    onChartShare?.(chart);
  };

  const handleExportChart = (chart: ChartData, format: 'png' | 'svg' | 'pdf' | 'csv') => {
    onChartExport?.(chart, format);
  };

  const filteredCharts = charts.filter(chart => {
    const matchesType = filterType === 'all' || chart.type === filterType;
    const matchesSearch = chart.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         chart.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         chart.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesType && matchesSearch;
  });

  const getChartIcon = (type: string) => {
    switch (type) {
      case 'bar': return BarChart3;
      case 'line': return LineChart;
      case 'pie': return PieChart;
      case 'area': return Activity;
      case 'scatter': return Target;
      case 'radar': return Zap;
      case 'doughnut': return Circle;
      case 'polar': return Star;
      default: return BarChart3;
    }
  };

  const getChartColor = (type: string) => {
    switch (type) {
      case 'bar': return 'text-blue-600 dark:text-blue-400';
      case 'line': return 'text-green-600 dark:text-green-400';
      case 'pie': return 'text-purple-600 dark:text-purple-400';
      case 'area': return 'text-orange-600 dark:text-orange-400';
      case 'scatter': return 'text-red-600 dark:text-red-400';
      case 'radar': return 'text-yellow-600 dark:text-yellow-400';
      case 'doughnut': return 'text-pink-600 dark:text-pink-400';
      case 'polar': return 'text-indigo-600 dark:text-indigo-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getChartBgColor = (type: string) => {
    switch (type) {
      case 'bar': return 'bg-blue-100 dark:bg-blue-900';
      case 'line': return 'bg-green-100 dark:bg-green-900';
      case 'pie': return 'bg-purple-100 dark:bg-purple-900';
      case 'area': return 'bg-orange-100 dark:bg-orange-900';
      case 'scatter': return 'bg-red-100 dark:bg-red-900';
      case 'radar': return 'bg-yellow-100 dark:bg-yellow-900';
      case 'doughnut': return 'bg-pink-100 dark:bg-pink-900';
      case 'polar': return 'bg-indigo-100 dark:bg-indigo-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const renderChart = (chart: ChartData) => {
    // In a real app, this would use a charting library like Chart.js or D3.js
    // For now, we'll create a simple visual representation
    const maxValue = Math.max(...chart.data);
    const minValue = Math.min(...chart.data);

    return (
      <div className="w-full h-64 bg-gray-50 dark:bg-gray-800 rounded-lg p-4 flex items-end justify-center space-x-2">
        {chart.data.map((value, index) => {
          const height = ((value - minValue) / (maxValue - minValue)) * 200;
          const color = chart.colors[index % chart.colors.length];
          
          return (
            <div key={index} className="flex flex-col items-center">
              <div
                className="w-8 rounded-t"
                style={{
                  height: `${height}px`,
                  backgroundColor: color
                }}
              />
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                {chart.labels[index] || `Item ${index + 1}`}
              </div>
              <div className="text-xs font-medium text-gray-900 dark:text-white mt-1">
                {value}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Data Visualization
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Create and interact with charts and graphs to visualize your reading data
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search charts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="bar">Bar Charts</option>
              <option value="line">Line Charts</option>
              <option value="pie">Pie Charts</option>
              <option value="area">Area Charts</option>
              <option value="scatter">Scatter Plots</option>
              <option value="radar">Radar Charts</option>
              <option value="doughnut">Doughnut Charts</option>
              <option value="polar">Polar Charts</option>
            </select>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Chart
            </button>
          </div>
        </div>
      </div>

      {/* Charts Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCharts.map((chart) => {
            const ChartIcon = getChartIcon(chart.type);
            return (
              <div
                key={chart.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${getChartBgColor(chart.type)}`}>
                    <ChartIcon className={`h-6 w-6 ${getChartColor(chart.type)}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getChartBgColor(chart.type)} ${getChartColor(chart.type)}`}>
                      {chart.type}
                    </span>
                    {chart.isPublic && (
                      <Globe className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {chart.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {chart.description}
                </p>

                {/* Chart Preview */}
                <div className="mb-4">
                  {renderChart(chart)}
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {chart.tags.map(tag => `#${tag}`).join(' ')}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedChart(chart)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleShareChart(chart)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteChart(chart.id)}
                      className="p-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCharts.map((chart) => {
            const ChartIcon = getChartIcon(chart.type);
            return (
              <div
                key={chart.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${getChartBgColor(chart.type)}`}>
                      <ChartIcon className={`h-6 w-6 ${getChartColor(chart.type)}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {chart.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {chart.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getChartBgColor(chart.type)} ${getChartColor(chart.type)} mb-2`}>
                        {chart.type}
                      </span>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {chart.data.length} data points
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedChart(chart)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleShareChart(chart)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      >
                        <Share2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteChart(chart.id)}
                        className="p-2 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredCharts.length === 0 && (
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No charts found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first chart to visualize your reading data
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Create Chart
          </button>
        </div>
      )}

      {/* Create Chart Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Create New Chart
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Chart Type
                </label>
                <select
                  value={newChart.type}
                  onChange={(e) => setNewChart(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                  <option value="area">Area Chart</option>
                  <option value="scatter">Scatter Plot</option>
                  <option value="radar">Radar Chart</option>
                  <option value="doughnut">Doughnut Chart</option>
                  <option value="polar">Polar Chart</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={newChart.title || ''}
                  onChange={(e) => setNewChart(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter chart title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newChart.description || ''}
                  onChange={(e) => setNewChart(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter chart description"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data (comma-separated values)
                </label>
                <input
                  type="text"
                  value={newChart.data?.join(', ') || ''}
                  onChange={(e) => setNewChart(prev => ({ 
                    ...prev, 
                    data: e.target.value.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v))
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 10, 20, 30, 40, 50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Labels (comma-separated)
                </label>
                <input
                  type="text"
                  value={newChart.labels?.join(', ') || ''}
                  onChange={(e) => setNewChart(prev => ({ 
                    ...prev, 
                    labels: e.target.value.split(',').map(v => v.trim()).filter(v => v.length > 0)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Jan, Feb, Mar, Apr, May"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={newChart.isPublic || false}
                  onChange={(e) => setNewChart(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="mr-2 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isPublic" className="text-sm text-gray-700 dark:text-gray-300">
                  Make this chart public
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChart}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Chart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataVisualization;
